import React, { useEffect, useState } from "react";
import "../App.css";
import { getTokenInternal } from "./useToken";
import Nav from "./Navigation.js";

export default function MyItems() {
  const [myItems, setMyItems] = useState([]);
  const [itemVisibility, setItemVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [uncheckedUsers, setUncheckedUsers] = useState([]);
  const [showUncheckedOnly, setShowUncheckedOnly] = useState(false);
  const [showCheckedOnly, setShowCheckedOnly] = useState(false);
  const [allFriends, setAllFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalImageSrc, setModalImageSrc] = useState(null); // State for modal image

  const fetchUserData = async () => {
    try {
      const token = await getTokenInternal();
      if (token) {
        const url = `${process.env.REACT_APP_API_HOST}/users/${token.account.id}/items`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const completeList = await response.json();

          const filteredList = completeList.filter(
            (item) => item.games_id === 1
          );

          const initialVisibility = filteredList.reduce((acc, item) => {
            acc[item.name] = false;
            return acc;
          }, {});
          setItemVisibility(initialVisibility);
          setMyItems(filteredList);
        } else {
          console.error("Error fetching data:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelection = (e) => {
    const selectedValue = e.target.value;
    setSelectedEvent(selectedValue);
  };

  const handleClearSelection = () => {
    setSelectedEvent("");
  };

  const toggleVisibility = (itemName) => {
    setItemVisibility((prevVisibility) => {
      const newVisibility = Object.keys(prevVisibility).reduce((acc, key) => {
        acc[key] = key === itemName ? !prevVisibility[key] : false;
        return acc;
      }, {});
      return newVisibility;
    });
  };

  const handleToggleChecked = async (userItemId, isChecked) => {
    try {
      const token = await getTokenInternal();

      if (token) {
        const selectedItem = myItems.find(
          (item) => item.user_item_id === userItemId
        );

        if (selectedItem) {
          const url = `${process.env.REACT_APP_API_HOST}/users/${token.account.id}/items/${userItemId}/update`;
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              checked: !isChecked,
            }),
          });

          if (response.ok) {
            setMyItems((prevItems) =>
              prevItems.map((item) =>
                item.user_item_id === userItemId
                  ? { ...item, checked: !isChecked }
                  : item
              )
            );
          } else {
            console.error("Error updating isChecked:", response.statusText);
          }
        } else {
          console.error("Item not found in myItems array");
        }
      }
    } catch (error) {
      console.error("Error updating isChecked:", error.message);
    }
  };

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/friendsList`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setAllFriends(data);
          }
        }
      } catch (error) {
        console.error("Error fetching pending friend requests:", error);
      }
    };

    fetchFriends();
  }, []);

  const handleButtonClick = async (item_id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/items/${item_id}/users/unchecked`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch unchecked users");
      }
      const data = await response.json();
      setUncheckedUsers(data);
    } catch (error) {
      console.error(error.message);
    }
  };

  const combinedClickHandler = (itemName, itemId) => {
    toggleVisibility(itemName);
    handleButtonClick(itemId);
  };

  useEffect(() => {
    fetchUserData();
  }, [selectedEvent]);

  if (loading) {
    return <p>Loading...</p>;
  }

  const filterItemsBySearchQuery = (items) =>
    items.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const totalItemsCount = myItems.length;
  const checkedItemsCount = myItems.filter((item) => item.checked).length;

  const filteredItemsToRender = showUncheckedOnly
    ? filterItemsBySearchQuery(myItems).filter((item) => !item.checked)
    : showCheckedOnly
    ? filterItemsBySearchQuery(myItems).filter((item) => item.checked)
    : filterItemsBySearchQuery(myItems);

  const nonEventItems = filteredItemsToRender.filter(
    (item) => item.event === "Primary"
  );
  const otherEvents = filteredItemsToRender.filter(
    (item) => item.event !== "Primary"
  );
  const selectedEvents = selectedEvent === "All Events"
    ? filteredItemsToRender.filter((item) => item.event !== "Primary")
    : filteredItemsToRender.filter((item) => item.event === selectedEvent);

  const groupItemsByEvent = (items) => {
    const groupedItems = items.reduce((acc, item) => {
      if (!acc[item.event]) {
        acc[item.event] = [];
      }
      acc[item.event].push(item);
      return acc;
    }, {});

    const sortedEvents = Object.keys(groupedItems).sort();

    return sortedEvents.map((event) => ({
      event,
      items: groupedItems[event],
    }));
  };

  const groupedSelectedEvents = groupItemsByEvent(selectedEvents);

  const handleToggleShowChecked = () => {
    setShowUncheckedOnly(false);
    setShowCheckedOnly(!showCheckedOnly);
  };

  const handleToggleShowUnchecked = () => {
    setShowCheckedOnly(false);
    setShowUncheckedOnly(!showUncheckedOnly);
  };

  const showModal = (imageSrc) => {
    setModalImageSrc(imageSrc);
  };

  const hideModal = () => {
    setModalImageSrc(null);
  };

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4">
        <div className="text-2xl font-bold mb-4">My Complete List of Items: To see Event Items you must select event from DROP DOWN Menu</div>
        <div className="text-xl font-bold mb-4">Hint: if you click on "Not Found" while you check off items it will remove them as you collect them</div>

        <div className="mb-4 flex">
          <div className="mr-2 font-bold">Filter:</div>
          <button
            onClick={handleToggleShowUnchecked}
            className="mr-2 p-1 border rounded"
          >
            Not Found
          </button>
          <button
            onClick={handleToggleShowChecked}
            className="p-1 border rounded"
          >
            Found
          </button>
          <button
            onClick={() => {
              setShowUncheckedOnly(false);
              setShowCheckedOnly(false);
            }}
            className="ml-2 p-1 border rounded"
          >
            Show All
          </button>
        </div>

        <div className="mb-4">
          <label htmlFor="eventDropdown" className="text-md font-bold">
            Select Event:
          </label>
          <select
            id="eventDropdown"
            onChange={handleEventSelection}
            value={selectedEvent}
            className="ml-2 p-1 border rounded text-black"
          >
            <option value="">None</option>
            <option value="All Events">All Events</option>
            {Array.from(new Set(otherEvents.map((item) => item.event))).map(
              (event) => (
                <option key={event} value={event}>
                  {event}
                </option>
              )
            )}
          </select>
          <button
            onClick={handleClearSelection}
            className="ml-2 p-1 border rounded"
          >
            Clear
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="p-1 border rounded text-black w-full"
          />
        </div>

        {/* Total items count at the top */}
        <div className="text-lg font-bold mb-4">
          {checkedItemsCount >= totalItemsCount
            ? "Collected All Items!!!!!"
            : `Total: ${checkedItemsCount}/${totalItemsCount} items found`}
        </div>

        <div>
          {groupedSelectedEvents.map(({ event, items }) => (
            <div key={event}>
              <h3 className="text-lg font-bold italic underline">{event}</h3>
              <div className="text-lg font-bold mb-2 ml-2">
                Items found: {items.filter((item) => item.checked).length}/
                {items.length}
              </div>
              <div className="flex flex-row flex-wrap mb-2">
                {Array.from(new Set(items.map((item) => item.types))).map((type) => (
                  <div key={type} className="mb-4 mb-6 ml-2">
                    {/* <h4 className="text-md font-bold mb-2">{type}</h4> */}
                    <h4 className="text-md font-bold mb-2">
                       {type} (
                       {
                         items.filter(
                           (item) => item.types === type && item.checked
                         ).length
                       }
                       /{items.filter((item) => item.types === type).length}
                       )
                     </h4>
                    <div className="grid gap-2">
                      {filterItemsBySearchQuery(items)
                        .filter((item) => item.types === type)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((filteredItem) => (
                          <div
                            className={`border p-1 rounded ${
                              filteredItem.checked ? "bg-green-500" : "bg-neutral-400"
                            } flex items-center`}
                            key={filteredItem.user_item_id}
                          >
                            <input
                              type="checkbox"
                              checked={filteredItem.checked}
                              onChange={() =>
                                handleToggleChecked(
                                  filteredItem.user_item_id,
                                  filteredItem.checked
                                )
                              }
                              className="h-4 w-4"
                            />
                            <div className="flex">
                              <div className="ml-2">{filteredItem.name}</div>
                            </div>
                            <button
                              className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                              onClick={() =>
                                combinedClickHandler(
                                  filteredItem.name,
                                  filteredItem.item_id
                                )
                              }
                            >
                              {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
                            </button>
                            {itemVisibility[filteredItem.name] && (
                              <div className="mt-2">
                                <div className="flex">
                                  <div> Map: </div>
                                  <div className="ml-2">{filteredItem.maps_name}</div>
                                </div>
                                  <img
                                    className="object-scale-down h-48 cursor-pointer"
                                    src={`/imagesitems/${filteredItem.url}`}
                                    alt="Description of the image"
                                    onClick={() => showModal(`/imagesitems/${filteredItem.url}`)}
                                    onError={(e) => {
                                      e.target.src = '/gamelogos/Unofficial.PNG'; // Set fallback image on error
                                    }}
                                  />
                                {uncheckedUsers.length > 0 && (
                                  <div>
                                    <h2>Who needs Cosmetic:</h2>
                                    <ul>
                                      {allFriends
                                        .filter((friend) =>
                                          uncheckedUsers.includes(friend.friend_username)
                                        )
                                        .sort(
                                          (a, b) =>
                                            uncheckedUsers.indexOf(
                                              a.friend_username
                                            ) -
                                            uncheckedUsers.indexOf(b.friend_username)
                                        )
                                        .map((friend, index) => (
                                          <li className="flex" key={index}>
                                            {friend.friend_username}
                                            {friend.visible ? (
                                              <div className="ml-2 flex">
                                                Discord:
                                                <div className="ml-2">
                                                  {friend.discord_name}
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="ml-2">Discord: N/A</div>
                                            )}
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="font-bold text-xl underline italic">Primary</p>
          {/* Primary section counts */}
         <div className="text-lg font-bold mb-2 ml-2">
          Items found: {nonEventItems.filter((item) => item.checked).length}/
           {nonEventItems.length}
           </div>
        <div>
          {Array.from(new Set(nonEventItems.map((item) => item.maps))).map((map) => (
            <div key={map} className="mb-6 ml-2">
              <h3 className="text-lg font-bold mb-2 italic underline">{map}</h3>
              <div className="flex flex-row flex-wrap mb-4">
                {Array.from(new Set(nonEventItems.filter((item) => item.maps === map).map((item) => item.types))).map((type) => (
                  <div key={type} className="mr-2">
                    <h4 className="text-md font-bold mb-2">
                      {type} (
                      {
                        nonEventItems.filter(
                          (item) => item.types === type && item.checked
                        ).length
                      }
                      /
                      {nonEventItems.filter((item) => item.types === type).length})
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {nonEventItems.filter((item) => item.maps === map && item.types === type).map((filteredItem) => (
                        <div
                          className={`border p-1 rounded ${
                            filteredItem.checked ? "bg-green-500" : "bg-neutral-400"
                          } flex items-center`}
                          key={filteredItem.user_item_id}
                        >
                          <input
                            type="checkbox"
                            checked={filteredItem.checked}
                            onChange={() =>
                              handleToggleChecked(
                                filteredItem.user_item_id,
                                filteredItem.checked
                              )
                            }
                            className="h-4 w-4"
                          />
                          <div className="flex">
                            <div className="ml-2">{filteredItem.name}</div>
                          </div>
                          <button
                            className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                            onClick={() =>
                              combinedClickHandler(
                                filteredItem.name,
                                filteredItem.item_id
                              )
                            }
                          >
                            {itemVisibility[filteredItem.name] ? "Hide" : "Info"}
                          </button>
                          {itemVisibility[filteredItem.name] && (
                            <div className="mt-2">
                              <div className="flex">
                                <div> Map: </div>
                                <div className="ml-2">{filteredItem.maps_name}</div>
                              </div>
                                <img
                                    className="object-scale-down h-48 cursor-pointer"
                                    src={`/imagesitems/${filteredItem.url}`}
                                    alt="Description of the image"
                                    onClick={() => showModal(`/imagesitems/${filteredItem.url}`)}
                                    onError={(e) => {
                                      e.target.src = '/gamelogos/Unofficial.PNG'; // Set fallback image on error
                                    }}
                                />
                              <ul>
                                <h2>Who needs Cosmetic:</h2>
                                {allFriends
                                  .filter((friend) =>
                                    uncheckedUsers.includes(
                                      friend.friend_username
                                    )
                                  )
                                  .sort(
                                    (a, b) =>
                                      uncheckedUsers.indexOf(
                                        a.friend_username
                                      ) -
                                      uncheckedUsers.indexOf(b.friend_username)
                                  )
                                  .map((friend, index) => (
                                    <li className="flex" key={index}>
                                      {friend.friend_username}
                                      {friend.visible ? (
                                        <div className="ml-2 flex">
                                          Discord:
                                          <div className="ml-2">
                                            {friend.discord_name}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="ml-2">Discord: N/A</div>
                                      )}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for image */}
      {modalImageSrc && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4" onClick={hideModal}>
          <img
            src={modalImageSrc}
            alt="Modal Content"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        </div>
      )}
    </div>
  );
}
